import React, {useEffect, useRef, useState} from 'react';
import {
  FlatList,
  Keyboard,
  KeyboardEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  StyleSheet,
  TextInputSubmitEditingEventData,
  View,
} from 'react-native';
import Geolocation from 'react-native-geolocation-service';
import {useDebounce} from 'use-debounce';

import {Icon} from '../../components/Icon';
import Button from '../../components/ui-controls/Button';
import CrossPlatformModal from '../../components/ui-controls/CrossPlatformModal';
import TextInput, {TextInputRef} from '../../components/ui-controls/form/TextInput';
import Text from '../../components/ui-controls/Text';
import {useModalDimensions} from '../../hooks/useModalDimensions';
import {GeocodedSuburb} from '../../models/middleware';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {suburbKey} from '../../utils/address';
import {logger, serializeError} from '../../utils/logger';
import {requestFineLocationAccessPermission} from '../../utils/permissions/permissions';

/**
 * Floor for the Android keyboard-bounded modal height (DIS-1552, decision 4).
 * A device reporting an unexpectedly tall IME must never subtract the modal down
 * to zero and render it invisible; this keeps the title, the search input and the
 * geolocation button on screen even in that case.
 */
const ANDROID_KEYBOARD_MIN_CONTENT_HEIGHT = 240;

const SearchBarSearchIcon = () => (
  <View focusable={false} pointerEvents="none">
    <Icon name="magnifying-glass" color="lightgrey" />
  </View>
);

const SearchBarCloseIcon = ({onPress}: {onPress: () => void}) => (
  <Pressable onPress={onPress}>
    <Icon name="circle-xmark" color="lightgrey" style={{marginLeft: 3}} />
  </Pressable>
);

const Separator = () => (
  <View
    style={{
      marginVertical: 4,
      borderBottomWidth: 1,
      borderBottomColor: 'lightgrey',
    }}
  />
);

type SuburbSelectionModalProps = {
  bodyText?: string;
  contentStyle?: object;
  extraContent?: React.ReactNode;
  isVisible: boolean;
  onCancel?: () => void;
  onSelectSuburb: (suburb: GeocodedSuburb) => void;
  title: string;
};
const SuburbSelectionModal = ({
  bodyText,
  contentStyle,
  extraContent,
  isVisible,
  onCancel,
  onSelectSuburb,
  title = 'FIND YOUR STORE',
}: SuburbSelectionModalProps) => {
  const [geolocationError, setGeolocationError] = useState<string>('');
  const {shopSelectionStore} = useStores();

  const [isSearching, setSearching] = useState(false);
  const [selectedSuburbValue, setSelectedSuburbValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const {containerStyle: modalContainerStyle, maxHeight: modalMaxHeight} = useModalDimensions({
    size: 'large',
    constrainHeight: true,
  });

  // Reset state when modal opens so each session starts fresh.
  // Clear suburbList on OPEN (not close) — StoreSelectionScreen reads it
  // after suburb selection, so it must persist through the close transition.
  useEffect(() => {
    if (isVisible) {
      setSearchText('');
      setSelectedSuburbValue('');
      setGeolocationError('');
      setSearching(false);
      shopSelectionStore.clearSuburbList();
    } else {
      setIsKeyboardVisible(false);
      setKeyboardHeight(0);
    }
  }, [isVisible, shopSelectionStore]);

  // Track IME so Android back can dismiss the keyboard before closing the modal,
  // and record its height so Android can bound the modal against it (DIS-1552).
  // One subscription pair only: a second pair would double every keyboard-driven
  // re-render in a component that DIS-1519 showed is sensitive to exactly that.
  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (event?: KeyboardEvent) => {
      setIsKeyboardVisible(true);
      // Read defensively: RN can deliver an event without endCoordinates, and
      // tests invoke the registered handler with no argument at all.
      setKeyboardHeight(event?.endCoordinates?.height ?? 0);
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setIsKeyboardVisible(false);
      setKeyboardHeight(0);
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const handleSelectSuburb = (suburb: GeocodedSuburb) => {
    setSelectedSuburbValue(suburb.locality);
    setSearchText('');

    onSelectSuburb(suburb);
  };

  useEffect(() => {
    async function getSuburbList() {
      if (debouncedSearchText) {
        setSearching(true);
        shopSelectionStore.clearSuburbList();
        await shopSelectionStore.loadSuburbList(debouncedSearchText);
        setSearching(false);
      }
    }
    getSuburbList();
  }, [debouncedSearchText, shopSelectionStore]);

  const handleTextChanged = (text: string) => {
    // Remove anything that's not alphanumeric, space or hyphen.
    const filter = /[^0-9a-zA-Z- ']/gi;
    const newTerm = text.replace(filter, '');
    // Avoid API call for searchText same term
    if (newTerm !== searchText || newTerm === '') {
      setSearchText(newTerm);
      setSelectedSuburbValue('');
      if (newTerm && newTerm.length >= 3) {
        // show indicator immediately, independently of the debouncing
        setSearching(true);
      } else if (!newTerm) {
        setSearching(false);
        shopSelectionStore.loadSuburbList('');
      }
    }
  };

  const handleManualSearchPress = async ({
    nativeEvent: {text},
  }: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
    // Dismiss IME so search results are visible without needing Android back.
    setIsKeyboardVisible(false);
    setKeyboardHeight(0);
    Keyboard.dismiss();
    if (text && text.length >= 3) {
      // Own the loading flag locally around the load. This component is not a
      // MobX observer, so the setIsKeyboardVisible re-render above would latch
      // shopSelectionStore.suburbListLoading === true into "Searching..." and
      // never clear when the store finishes (no observer re-render). Driving the
      // local `isSearching` flag re-renders on completion. Mirrors getSuburbList.
      setSearching(true);
      await shopSelectionStore.loadSuburbList(text);
      setSearching(false);
    }
  };

  const handleClearText = () => {
    handleTextChanged('');
  };

  const handleCancel = () => onCancel?.();

  const handleBackButtonPress = () => {
    if (isKeyboardVisible) {
      setIsKeyboardVisible(false);
      setKeyboardHeight(0);
      Keyboard.dismiss();
      return;
    }
    handleCancel();
  };

  const handleGeolocationRequested = async () => {
    setSearching(true);
    const authorised = await requestFineLocationAccessPermission();
    if (!authorised) {
      logger.warn('SuburbSelectionModal', 'Geolocation permission not granted');
      setGeolocationError('Geolocation permission not granted');
      setSearching(false);
      return;
    }
    // selectSuburb({"id": "Beerwah QLD 4519", "label": "Beerwah QLD 4519", "lat": "-26.857043", "lon": "152.957162", "value": "Beerwah QLD 4519"});
    Geolocation.getCurrentPosition(
      async ({coords}) => {
        try {
          const suburbs = await shopSelectionStore.reverseGeocode(
            coords.latitude,
            coords.longitude,
          );

          if (suburbs.length === 1) {
            shopSelectionStore.resetLoading();
            handleSelectSuburb(suburbs[0]);
          }

          setSearching(false);
        } catch (err) {
          logger.error(
            'SuburbSelectionModal',
            'Error during reverse geocoding',
            serializeError(err),
          );
          setSearching(false);
          setGeolocationError('Something went wrong, please try again');
        }
      },
      error => {
        // error.code: PERMISSION_DENIED = 1
        setSearching(false);
        setGeolocationError(error.message);
      },
      {enableHighAccuracy: false, timeout: 10000, maximumAge: 10000},
    );
  };

  const renderSuburbListItem = ({item: suburb}: {item: GeocodedSuburb}) => (
    <Pressable onPress={() => handleSelectSuburb(suburb)} testID="suburb-list-item">
      <View style={styles.suburbRow}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.suburbText}>
          {`${suburb.locality} ${suburb.administrative_area} ${suburb.postal_code}`}
        </Text>
        <Icon name="chevron-right" size={12} color="lightgrey" />
      </View>
    </Pressable>
  );

  const renderSearchResults = () => {
    const maxResults = 8;

    if (isSearching || shopSelectionStore.suburbListLoading || shopSelectionStore.geocoding) {
      return <Text style={styles.loadingText}>Searching...</Text>;
    }

    if (!isSearching && searchText.length >= 3 && !shopSelectionStore.suburbList.length) {
      return (
        <Text style={styles.errorText}>No matching suburbs were found. Please try again.</Text>
      );
    }

    if (geolocationError) {
      return (
        <View>
          <Text
            style={
              styles.errorText
            }>{`Unable to retrieve current location: ${geolocationError}.`}</Text>
          <Button title="OK" onPress={() => setGeolocationError('')} />
        </View>
      );
    }

    // Only show results when there's an active search query.
    // Without this, stale results persist after state reset because
    // this component is not an observer (MobX changes don't trigger re-render).
    if (!searchText && !selectedSuburbValue) {
      return null;
    }

    const suburbList = shopSelectionStore.suburbList.slice(0, maxResults);

    return (
      <FlatList
        style={[styles.suburbScrollView, {flexShrink: 1}]}
        data={suburbList}
        keyExtractor={suburbKey}
        renderItem={renderSuburbListItem}
        ItemSeparatorComponent={Separator}
        keyboardShouldPersistTaps="always"
        keyboardDismissMode="on-drag"
      />
    );
  };

  // Android only. `avoidKeyboard` on the RN Modal is a no-op there and the
  // manifest is adjustPan, so the modal is sized against the full screen while
  // the IME covers the bottom of it.
  //
  // DIS-1693: the original DIS-1552 fix subtracted the IME height from maxHeight.
  // Everything above the FlatList — title, body copy, input, geolocation button —
  // is unshrinkable, so the list absorbed the whole reduction and measured ~0px:
  // no results rendered and nothing scrolled. Lift the box with marginBottom
  // instead, which moves the modal without taking height from its only flexible
  // child, and keep a maxHeight floor so a tall IME cannot push the box off the
  // top of the screen. iOS keeps `avoidKeyboard` and web is already handled
  // inside useModalDimensions, so neither adjusts here.
  const androidKeyboardVisible = Platform.OS === 'android' && keyboardHeight > 0;
  const androidKeyboardStyle =
    androidKeyboardVisible && modalMaxHeight !== undefined
      ? {
          marginBottom: keyboardHeight,
          maxHeight: Math.max(ANDROID_KEYBOARD_MIN_CONTENT_HEIGHT, modalMaxHeight - keyboardHeight),
        }
      : null;

  const userInput = searchText || selectedSuburbValue;

  const inputRef = useRef<TextInputRef>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isVisible && inputRef.current?.focus) {
      timer = setTimeout(() => inputRef.current?.focus(), 100);
    }
    return () => clearTimeout(timer);
  }, [isVisible]);

  return (
    <CrossPlatformModal
      isVisible={isVisible}
      backdropColor="black"
      backdropOpacity={0.5}
      hasBackdrop
      onBackdropPress={handleCancel}
      onBackButtonPress={handleBackButtonPress}
      avoidKeyboard
      animationInTiming={1}
      animationOutTiming={1}
      style={styles.suburbSelectionModal}
      webContainerStyle={styles.suburbSelectionModal}
      testID="suburb-selection-modal">
      <View
        testID="suburb-selection-modal-content"
        style={[
          styles.suburbSelectionModalContent,
          modalContainerStyle,
          androidKeyboardStyle,
          contentStyle,
        ]}>
        <Text style={styles.suburbSelectionModalContentTitle}>{title}</Text>
        {!!bodyText && <Text>{bodyText}</Text>}
        <TextInput
          ref={inputRef}
          autoFocus={false}
          leftIcon={<SearchBarSearchIcon />}
          maxLength={80}
          onChangeText={handleTextChanged}
          onSubmitEditing={handleManualSearchPress}
          placeholder="Enter your postcode or suburb"
          renderErrorMessage={false}
          returnKeyType="search"
          rightIcon={<SearchBarCloseIcon onPress={handleClearText} />}
          spellCheck={false}
          value={userInput}
          testID="suburb-search-input"
          containerStyle={styles.searchInputContainer}
          inputContainerStyle={styles.searchInputField}
        />
        <Button
          type="clear"
          title="Use your current location"
          onPress={handleGeolocationRequested}
          disabled={shopSelectionStore.geocoding}
          icon={
            <Icon
              name="location-crosshairs"
              size={16}
              color={
                isSearching
                  ? theme.colour.background.button.disabled
                  : theme.colour.uiControls.primary
              }
            />
          }
          titleStyle={styles.suburbText}
          buttonStyle={{width: 240}}
        />
        {!!extraContent && extraContent}
        {renderSearchResults()}
      </View>
    </CrossPlatformModal>
  );
};

export default SuburbSelectionModal;

const styles = StyleSheet.create({
  suburbSelectionModalContent: {
    backgroundColor: 'white',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: 'grey',
  },
  suburbScrollView: {
    width: '100%',
    marginTop: 8,
  },
  suburbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 5,
    left: 1,
  },
  suburbText: {
    fontSize: 16,
    marginLeft: 8,
    fontWeight: '400',
  },
  suburbSelectionModal: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  suburbSelectionModalContentTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
    paddingBottom: 16,
  },
  suburbSelectionModalContentBody: {
    flex: 1,
    alignItems: 'center',
  },
  suburbSelectionModalText: {
    textAlign: 'center',
  },
  loadingText: {
    textAlign: 'center',
    marginTop: 30,
  },
  errorText: {
    marginTop: 10,
  },
  searchInputContainer: {
    marginTop: 12,
    marginBottom: 4,
  },
  searchInputField: {
    borderBottomColor: '#d0d0d0',
    borderBottomWidth: 1,
  },
});
